/**
 * Type definitions for filter system
 */

/**
 * Controls which timestamp column the time filter is applied to.
 * - 'first_seen': filter on first_seen_at (inventory) / created_at (events/incidents)
 * - 'last_seen':  filter on last_seen_at (default)
 */
export type TimestampMode = 'first_seen' | 'last_seen'

/**
 * Controls how timestamps are displayed in columns and entered in the custom picker.
 * - 'utc':   show raw UTC values (default)
 * - 'local': show in browser's local timezone
 */
export type TimezoneMode = 'utc' | 'local'

export type TimeFilterPreset =
  | 'last_30m'
  | 'last_1h'
  | 'last_2h'
  | 'last_4h'
  | 'last_12h'
  | 'last_24h'
  | 'last_7d'
  | 'last_30d'
  | 'last_90d'

export const TIME_FILTER_PRESET_LABELS: Record<TimeFilterPreset, string> = {
  last_30m: 'Last 30 minutes',
  last_1h: 'Last 1 hour',
  last_2h: 'Last 2 hours',
  last_4h: 'Last 4 hours',
  last_12h: 'Last 12 hours',
  last_24h: 'Last 24 hours',
  last_7d: 'Last 7 days',
  last_30d: 'Last 30 days',
  last_90d: 'Last 90 days',
}

// Millisecond offsets for each preset, used by resolveTimeFilter
export const TIME_FILTER_PRESET_OFFSETS_MS: Record<TimeFilterPreset, number> = {
  last_30m: 30 * 60_000,
  last_1h: 60 * 60_000,
  last_2h: 2 * 60 * 60_000,
  last_4h: 4 * 60 * 60_000,
  last_12h: 12 * 60 * 60_000,
  last_24h: 24 * 60 * 60_000,
  last_7d: 7 * 24 * 60 * 60_000,
  last_30d: 30 * 24 * 60 * 60_000,
  last_90d: 90 * 24 * 60 * 60_000,
}

/**
 * Discriminated union for the global time filter state.
 * - mode 'none'   : no time filter applied
 * - mode 'preset' : rolling window computed at query-time from now, optionally shifted
 *   - shift 0 (default): [now - duration, now]
 *   - shift N > 0: [now - (N+1)*duration, now - N*duration]  (N intervals back)
 * - mode 'custom' : fixed UTC ISO-8601 start/end chosen by the user
 */
export type GlobalTimeFilter =
  | { mode: 'none' }
  | { mode: 'preset'; preset: TimeFilterPreset; shift?: number }
  | { mode: 'custom'; start: string; end: string }

/**
 * Show flags filter value - combines false positive and suppressed toggles
 * into a single filter value
 */
export type ShowFlagsValue = 'none' | 'false_positive' | 'suppressed' | 'both'

/**
 * Helper to compute API parameters from showFlags value
 */
export function unpackShowFlags(showFlags: ShowFlagsValue | undefined): {
  include_false_positive: boolean
  include_suppressed: boolean
} {
  const flags = showFlags || 'none'
  return {
    include_false_positive: flags === 'false_positive' || flags === 'both',
    include_suppressed: flags === 'suppressed' || flags === 'both',
  }
}

/**
 * Helper to compute showFlags value from two boolean states
 */
export function packShowFlags(
  includeFalsePositive: boolean,
  includeSuppressed: boolean,
): ShowFlagsValue {
  if (includeFalsePositive && includeSuppressed) return 'both'
  if (includeFalsePositive) return 'false_positive'
  if (includeSuppressed) return 'suppressed'
  return 'none'
}

/**
 * Helper to compute checkbox states from showFlags value
 */
export function getCheckboxStates(showFlags: ShowFlagsValue | undefined): {
  falsePositiveChecked: boolean
  suppressedChecked: boolean
} {
  const flags = showFlags || 'none'
  return {
    falsePositiveChecked: flags === 'false_positive' || flags === 'both',
    suppressedChecked: flags === 'suppressed' || flags === 'both',
  }
}

/**
 * Helper to update showFlags when false positive checkbox changes
 */
export function updateShowFlagsForFP(
  currentFlags: ShowFlagsValue,
  checked: boolean,
): ShowFlagsValue {
  const { suppressedChecked } = getCheckboxStates(currentFlags)

  if (checked) {
    return suppressedChecked ? 'both' : 'false_positive'
  } else {
    return suppressedChecked ? 'suppressed' : 'none'
  }
}

/**
 * Helper to update showFlags when suppressed checkbox changes
 */
export function updateShowFlagsForSuppressed(
  currentFlags: ShowFlagsValue,
  checked: boolean,
): ShowFlagsValue {
  const { falsePositiveChecked } = getCheckboxStates(currentFlags)

  if (checked) {
    return falsePositiveChecked ? 'both' : 'suppressed'
  } else {
    return falsePositiveChecked ? 'false_positive' : 'none'
  }
}
